import type { ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Chart from "@/v2-ui/chart"
import Badge from "@/v2-backoffice-client/components/badge"
import PortfolioSectionHeader from "@/v2-backoffice-client/portfolio/PortfolioSectionHeader"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import UsageAppMix, {
  type UsageAppMixEntry,
} from "@/v2-backoffice-client/usage/UsageAppMix"
import { APP_MIX_TOOLTIP } from "@/v2-backoffice-client/usage/usage.constants"
import {
  MIX_STATE_CHART_COLORS,
  MIX_STATE_LABELS,
  MIX_STATE_ORDER,
  type PortfolioMixState,
} from "@/v2-backoffice-client/portfolio/portfolio.constants"

export type PortfolioMixSectionProps = ComponentProps<"section"> & {
  /** Fleet-wide per-app mix (with exact trends from the daily rows). */
  entries: UsageAppMixEntry[]
  /** Customers per product-mix state — the migration funnel. */
  counts: Record<PortfolioMixState, number>
  /** Vacation-overlap warning for the trend comparisons, when applicable. */
  trendSeasonNote?: string | null
}

const CHART_CONFIG = {}

// Fleet product mix + migration funnel. The split bar says where the
// portfolio's activity lives (v1 amber vs v2 green); the funnel says where
// each CUSTOMER stands on the journey — every v1-only customer is a v2 demo
// candidate, every dual customer a migration to support.
function PortfolioMixSection(props: PortfolioMixSectionProps) {
  const { entries, counts, trendSeasonNote, className, ...sectionProps } = props

  const funnel = MIX_STATE_ORDER.map((state) => ({
    state,
    label: MIX_STATE_LABELS[state],
    count: counts[state],
    fill: MIX_STATE_CHART_COLORS[state],
  }))

  return (
    <section className={cn("flex flex-col gap-4", className)} {...sectionProps}>
      <PortfolioSectionHeader
        eyebrow="Products"
        title="Product mix"
        lead="Which products carry the activity, and how far the customers have come on the move from Console v1 to v2."
        info={APP_MIX_TOOLTIP}
      >
        {trendSeasonNote != null && (
          <Badge
            className="ml-auto"
            variant="warning"
            tooltip={trendSeasonNote}
          >
            vacation overlap
          </Badge>
        )}
      </PortfolioSectionHeader>

      <UsageReportCard className="flex-1">
        <UsageAppMix entries={entries} />

        <span className="mt-1 text-xs font-medium text-muted-foreground">
          Customers on the migration journey
        </span>
        <Chart config={CHART_CONFIG} className="h-[118px] w-full">
          <Chart.BarChart
            data={funnel}
            layout="vertical"
            margin={{ left: 0, right: 34, top: 2, bottom: 2 }}
          >
            <Chart.Axis.X type="number" hide />
            <Chart.Axis.Y
              type="category"
              dataKey="label"
              width={104}
              tickLine={false}
              axisLine={false}
              className="text-xs text-muted-foreground"
            />
            <Chart.Bar
              dataKey="count"
              radius={2}
              maxBarSize={14}
              isAnimationActive={false}
            >
              {funnel.map((entry) => (
                <Chart.BarCell key={entry.state} fill={entry.fill} />
              ))}
              <Chart.LabelList
                dataKey="count"
                position="right"
                formatter={(value: unknown) => formatNumber(Number(value))}
                className="fill-muted-foreground"
                fontSize={11}
              />
            </Chart.Bar>
          </Chart.BarChart>
        </Chart>
      </UsageReportCard>
    </section>
  )
}

export default PortfolioMixSection
